import React, { useContext } from 'react';
import { StockLocationRoleAssignmentContext } from '../../../../../context/StockLocationRoleAssignmentContext';
import {
  DeleteStockLocationRoleAssignmentResponse,
  DeleteStockLocationRoleAssignmentVariables,
  StockLocationRoleAssignmentMutation,
} from '../../../../../graphql/stockLocationRoleAssignment.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { useToast } from '../../../../../providers/ToastProvider';
import { StockLocationRoleAssignmentInput } from '../../../../../types/stock_location_role_assignment';
import { StockLocationRoleAssignment } from '../../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { User } from '../../../../../types/user/user';
import { Button, ButtonVariant, CancelButton } from '../../../../components/Button';
import { AlertModal } from '../../../../components/Modal';
import { ToastType } from '../../../../components/Toast';

interface DeleteStockLocationRolesForUserModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  assignments: Map<string, StockLocationRoleAssignment>;
  user: User;
}

export default function DeleteStockLocationRolesForUserModal({
  open,
  setOpen,
  assignments,
  user,
}: DeleteStockLocationRolesForUserModalProps) {
  const { showToast } = useToast();

  const { stockLocationRoles, setStockLocationRoles } = useContext(StockLocationRoleAssignmentContext);

  const [remove, { loading }] = useMutation<
    DeleteStockLocationRoleAssignmentResponse,
    DeleteStockLocationRoleAssignmentVariables
  >(StockLocationRoleAssignmentMutation.remove, {
    variables: {
      stockLocationRoleAssignments: [...assignments.values()].map(a =>
        new StockLocationRoleAssignmentInput(a).forDelete(),
      ),
    },
    onCompleted: response => {
      response.deleteStockLocationRoleAssignment.forEach(a => {
        const forLocation = stockLocationRoles.get(a.stockLocationId);
        if (!forLocation) return;
        stockLocationRoles.set(
          a.stockLocationId,
          forLocation.filter(la => la.id !== a.id),
        );
      });
      setStockLocationRoles(new Map(stockLocationRoles));
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.stockLocationRoleAssignmentRemovedSuccessfullyToastTitle).singular.upper,
      });
      setOpen(false);
    },
  });

  return (
    <AlertModal
      title={t(TranslationKey.deleteStockLocationRolesModalTitle).singular.upper}
      open={open}
      setOpen={setOpen}
    >
      <div className='grid gap-4'>
        <p className='subtitle'>{t(TranslationKey.deleteStockLocationRolesModalSubtitle).singular.upper}</p>
        <div className='flex justify-end gap-2'>
          <CancelButton onClick={() => setOpen(false)} disabled={loading} />
          <Button
            variant={ButtonVariant.danger}
            text={t(TranslationKey.delete).singular.upper}
            onClick={() => remove()}
            loading={loading}
          />
        </div>
      </div>
    </AlertModal>
  );
}
